import assign from 'lodash/fp/assign';
import flow from 'lodash/fp/flow';
import omit from 'lodash/fp/omit';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';

import {ORG_ADMIN_ROLE_NAME, RISK_MANAGER_ROLE_NAME} from '../../constants/users';
import {removeEmptyStrings} from '../form-utils';

/**
 * @typedef {{firstName:string, lastName:string}} UserDetails
 */

/**
 * Returns `${user.firstName} ${user.lastName}`.
 * @param {UserDetails} user
 * @return {string}
 */
export function formUsername(user) {
  const firstName = get(user, 'firstName', '');
  const lastName = get(user, 'lastName', '');

  return `${firstName} ${lastName}`;
}

/**
 * Creating names WITHOUT the middle name
 * @param {UserDetails} user
 * @param {boolean} lastFirst Whether to show the last name first
 * @return {string}
 */
export function formName(user, lastFirst = true) {
  let {firstName, lastName} = user;

  //This trims any trailing spaces at the beginning or end of the names. In the case of a name with only spaces in it, it becomes an empty string.
  firstName = isString(firstName) ? firstName.trim() : firstName;
  lastName = isString(lastName) ? lastName.trim() : lastName;

  if (!isEmpty(firstName) || !isEmpty(lastName)) {
    //At least one name is defined and is not an empty string

    if (lastFirst) {
      //Includes a comma "separator" only if both names are defined and the last name is shown first
      const separator = !isEmpty(firstName) && !isEmpty(lastName) ? ',' : '';
      return `${lastName ?? ''}${separator} ${firstName ?? ''}`.trim();
    } else {
      return `${firstName ?? ''} ${lastName ?? ''}`.trim();
    }
  }
}

/**
 * Creating names WITH the middle name
 * @param {UserDetails} user
 * @param {boolean} lastFirst Whether to show the last name first
 * @return {string}
 */
export function formFullName(user, lastFirst = true) {
  const lastName = get(user, 'lastName', '');
  const firstName = get(user, 'firstName', '');
  const middleName = get(user, 'middleName', '');
  if (lastFirst) {
    return middleName ? `${lastName}, ${firstName} ${middleName}` : `${lastName}, ${firstName}`;
  } else {
    return middleName ? `${firstName} ${middleName} ${lastName}` : `${firstName} ${lastName}`;
  }
}

export function setUsersPersonName(user, lastFirst = true) {
  return {
    ...user,
    fullName: formFullName(user, lastFirst),
    // get() returns null for null values so we need to check for falsey values returned from get()
    middleName: get(user, 'middleName') || '',
  };
}

export function formatUpdateUserPayload(person) {
  return flow([assign({username: person.primaryEmail}), omit('primaryEmail')])(removeEmptyStrings(person));
}

/**
 * GET organizations/:orgId/users/:userId returns a flat list of users role assignments
 * this function rolls up the role assignments, builds a map where org ID is the key for all user's role assignments as well as a list with just the values
 * @param {*} person user data response from users-ms
 * @returns object org permission map + list
 */
export function rollupOrganizationPermissions(person) {
  const organizationPermissionsMap = {};
  const roleAssignments = get(person, 'roleAssignments', []);
  roleAssignments.map((roleAssignment) => {
    const {organizationRole} = roleAssignment;
    const {organization} = organizationRole;
    if (organizationPermissionsMap[organization.id]) {
      organizationPermissionsMap[organization.id].roleAssignments.push(roleAssignment);
    } else {
      organizationPermissionsMap[organization.id] = {
        ...organization,
        roleAssignments: [roleAssignment],
      };
    }
    return roleAssignment;
  });
  const list = Object.values(organizationPermissionsMap);

  return {map: organizationPermissionsMap, list};
}

function getRoleIdByRoleName(roles, roleName) {
  const role = roles?.find((role) => role?.roleDefinition?.name === roleName);
  const roleId = role?.roleDefinition?.id;
  return roleId;
}

export function getRiskManagerRoleId(roles) {
  return getRoleIdByRoleName(roles, RISK_MANAGER_ROLE_NAME);
}

export function getOrgAdminRoleId(roles) {
  return getRoleIdByRoleName(roles, ORG_ADMIN_ROLE_NAME);
}
